import { Injectable } from '@angular/core';
import { HttpErrorHandler, HandleError } from '@app/shared/services/http-error-handler.service';
import { HttpClient, HttpParameterCodec, HttpParams } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { HeadersSetterHelper } from '@app/shared/helpers/http-headers-setter-helper';
import { BehaviorSubject, Observable } from 'rxjs';
import { EmployeesServicesResponse, ISpringResponseBody, observeTypes, RequestsWebIdentificationResponse, WebIdentificationHistoryResponse } from '@app/shared/interfaces/response-interfaces';
import { AppConfig } from '@app/config/app.config';
import { catchError } from 'rxjs/operators';
import { IBusinessUser, IChangeStatusRequest, IGroupRequest } from './business-models';
import { SearchCertificateCriteria } from './business-hardware-certificates/hardware-certificates.interface';
import { DatePipe } from '@angular/common';
import { IParamsForReceiptRequest } from './business-history/business-requested-history/business-requested-history.models';

@Injectable()
export class BusinessService {
  private handleError: HandleError;
  private headerHelper: HeadersSetterHelper;

  updateUserTable = new BehaviorSubject<boolean>(false);
  updateGroupTable = new BehaviorSubject<boolean>(false);
  updateNavBar = new BehaviorSubject<boolean>(false);

  private identificatorDataSubject = new BehaviorSubject<any>([]);
  identificatorData$ = this.identificatorDataSubject.asObservable();

  constructor(
    private httpErrorHandler: HttpErrorHandler,
    private http: HttpClient,
    private store: Store<any>
  ) {
    this.handleError = httpErrorHandler.createHandleError('BusinessService');
    this.headerHelper = new HeadersSetterHelper(this.store);
  }

  getCompanies(personId: string): Observable<ISpringResponseBody | any> {
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.companies.replace('{personId}', `${personId}`),
      this.headerHelper.options((observeTypes.BODY))
    ).pipe(
      catchError(this.handleError('getCompanies'))
    );
  }

  getCompany(companyId: string): Observable<ISpringResponseBody | any> {
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.company.replace('{companyId}', `${companyId}`),
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('getCompany'))
    );
  }

  getServices(companyCmrId?): Observable<ISpringResponseBody | any> {
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.services, this.headerHelper.addEmployeeOptions(observeTypes.BODY, { companyCmrId: companyCmrId })
    ).pipe(
      catchError(this.handleError('getServices'))
    );
  }
  getAvalibaleServicesUser(companyId: string, cmrId: any): Observable<ISpringResponseBody | any> {
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.servicesAvailableUser.replace('{companyId}', `${companyId}`).replace('{cmrId}', `${cmrId}`),
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('getServices'))
    );
  }
  getEmployeesServices(companyId: string, cmrId: any): Observable<any> {
    return this.http.get<any>(
      AppConfig.endpoints.employeesServices.replace('{companyId}', `${companyId}`).replace('{cmrId}', `${cmrId}`),
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('getUsers'))
    );
  }
  getUsers(companyId: string): Observable<ISpringResponseBody | any> {
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.employees.replace('{companyId}', `${companyId}`),
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('getUsers'))
    );
  }

  getUserInfo(identifier: any): Observable<ISpringResponseBody | any> {
    return this.http.post<ISpringResponseBody>(
      AppConfig.endpoints.person, identifier, this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('userInfo'))
    );
  }

  deleteUser(customerUserId: string): Observable<ISpringResponseBody | any> {
    return this.http.delete<ISpringResponseBody>(
      AppConfig.endpoints.deleteEmployee.replace('{customerUserId}', `${customerUserId}`), this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('deleteUser'))
    );
  }

  changeUserStatus(changeStatusRequest: IChangeStatusRequest): Observable<ISpringResponseBody | any> {
    return this.http.put<ISpringResponseBody>(
      AppConfig.endpoints.employeeChangeStatus, changeStatusRequest, this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('blockUser'))
    );
  }

  removeUserFromGroup(customerUserId: string, groupId: string): Observable<ISpringResponseBody | any> {
    return this.http.delete<ISpringResponseBody>(
      AppConfig.endpoints.removeUserFromGroup.replace('{customerUserId}', `${customerUserId}`).replace('{groupId}', `${groupId}`), this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('removeUser'))
    );
  }

  addUser(user: IBusinessUser, companyCmrId: string): Observable<ISpringResponseBody | any> {
    return this.http.post<ISpringResponseBody>(
      AppConfig.endpoints.addEmployee, user, this.headerHelper.addEmployeeOptions(observeTypes.BODY, { companyCmrId: companyCmrId })
    ).pipe(
      catchError(this.handleError('addUser'))
    );
  }

  updateUser(user: IBusinessUser): Observable<ISpringResponseBody | any> {
    return this.http.put<ISpringResponseBody>(
      AppConfig.endpoints.editEmployee, user, this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('editUser'))
    );
  }

  getCompanyGroups(companyCmrId: string): Observable<ISpringResponseBody | any> {
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.allGroups.replace('{companyCmrId}', `${companyCmrId}`),
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('getGroups'))
    );
  }

  getEmployeeCompanyGroups(employeeCmrId: string, companyCmrId: string): Observable<ISpringResponseBody | any> {
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.employeeGroups.replace('{companyCmrId}', `${companyCmrId}`).replace('{employeeCmrId}', `${employeeCmrId}`),
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('getGroups'))
    );
  }

  updateGroup(group: IGroupRequest): Observable<ISpringResponseBody | any> {
    return this.http.put<ISpringResponseBody>(
      AppConfig.endpoints.group, group, this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('editGroup'))
    );
  }

  addGroup(group: IGroupRequest): Observable<ISpringResponseBody | any> {
    return this.http.post<ISpringResponseBody>(
      AppConfig.endpoints.group, group, this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('addGroup'))
    );
  }

  deleteGroup(groupId: string): Observable<ISpringResponseBody | any> {
    return this.http.delete<ISpringResponseBody>(
      AppConfig.endpoints.deleteGroup.replace('{groupId}', `${groupId}`), this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('deleteGroup'))
    );
  }

  getRequestsHistoryDetails(requestedId: string): Observable<ISpringResponseBody | any> {
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.parcelDetails.replace('{parcelId}', `${requestedId}`),
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('getParcelDetails'))
    );
  }
  rejectParcel(parcelId: number, rejectorCmrId: string, reason: string): Observable<ISpringResponseBody | any> {
    return this.http.post<ISpringResponseBody>(
      AppConfig.endpoints.parcelReject,
      {
        parcelId,
        rejectorCmrId,
        reason
      },
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('rejectParcel'))
    );
  }
  setViewed(requestedId: string): Observable<ISpringResponseBody | any> {
    return this.http.post<ISpringResponseBody>(
      AppConfig.endpoints.viewed.replace('{parcelId}', `${requestedId}`), null,
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('setViewedStatus'))
    );
  }


  getDownloadDocIds(parcelId: string): Observable<ISpringResponseBody | any> {
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.parcelDocIds.replace('{parcelId}', `${parcelId}`),
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('getDownloadDocIds'))
    );
  }  


  getRegisteredMailReceipt(options: IParamsForReceiptRequest): Observable<ISpringResponseBody | any> {
    return this.http.get<ISpringResponseBody>(AppConfig.endpoints.operations.downloadReceipt, this.buildRequestOptions(options)
    ).pipe(
      catchError(this.handleError('getRegisteredMailReceipt'))
    );
  }

  getReceiversLists(companyCmrId: string, employeeCmrId: string): Observable<ISpringResponseBody | any> {
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.receiversLists
        .replace('{companyCmrId}', `${companyCmrId}`)
        .replace('{employeeCmrId}', `${employeeCmrId}`),
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('getReceiversLists'))
    );
  }

  getReceiversList(recipientListId: string): Observable<ISpringResponseBody | any> {
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.receiversList.replace('{recipientListId}', `${recipientListId}`),
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('getReceiversLists'))
    );
  }

  saveReceiversList(data: any): Observable<ISpringResponseBody | any> {
    return this.http.post<ISpringResponseBody>(
      AppConfig.endpoints.addReceiversList, data, this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('addReceiversList'))
    );
  }

  updateReceiversList(data: any): Observable<ISpringResponseBody | any> {
    return this.http.put<ISpringResponseBody>(
      AppConfig.endpoints.updateReceiversList, data, this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('updateReceiversList'))
    );
  }

  deleteReceiversList(employeeCmrId: string, companyCmrId: string, recipientListId: string): Observable<ISpringResponseBody | any> {
    return this.http.delete<ISpringResponseBody>(
      AppConfig.endpoints.deleteReceiversList
        .replace('{employeeCmrId}', `${employeeCmrId}`)
        .replace('{companyCmrId}', `${companyCmrId}`)
        .replace('{recipientListId}', `${recipientListId}`),
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('deleteReceiversList'))
    );
  }

  getRecipientsNames(cmrIds: any[]): Observable<ISpringResponseBody | any> {
    return this.http.post<ISpringResponseBody>(
      AppConfig.endpoints.profile.getRecipientsNames, cmrIds, this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('getRecipientsNames'))
    );
  }

  // Business Hardware Certificates ////
  getCertificateStatisticData(params: HttpParams, eik: any): Observable<ISpringResponseBody | any> {
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.certificatesReportData.replace('{eik}', `${eik}`),
      this.headerHelper.paramsOptions(observeTypes.RESPONSE, params)
    ).pipe(
      catchError(this.handleError('getCertificateStatisticData'))
    );
  }

  getCertificateStatisticDataPages(searchCriteria: any, eik: any): Observable<ISpringResponseBody | any> {
    const params = {
      offset: searchCriteria.offset,
      limit: searchCriteria.limit,
      hasPaging: true
    };
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.certificatesReportData.replace('{eik}', `${eik}`),
      this.headerHelper.paramsOptions(observeTypes.RESPONSE, params)
    ).pipe(
      catchError(this.handleError('getCertificateStatisticData'))
    );
  }
  getCertificateStatisticDataSort(sortTable: any, companyId: any): Observable<any> {
    const params = {
      orderBy: sortTable.orderBy,
      order: sortTable.order,
      offset: sortTable.offset,
      limit: sortTable.limit,
      hasPaging: true
    };

    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.certificatesReportData.replace('{companyId}', `${companyId}`),
      this.headerHelper.paramsOptions(observeTypes.RESPONSE, params)
    ).pipe(
      catchError(this.handleError('getCertificateStatisticDataSort'))
    );
  }
  getCertificateStatisticDataInExcelFormat(searchCriteria: SearchCertificateCriteria, eik: any): Observable<any> {
    const params = {
      hasPaging: false,
      ...searchCriteria
    };
    return this.http.get(
      AppConfig.endpoints.excellFormatReport.replace('{eik}', `${eik}`),
      {
        headers: this.headerHelper.downloadExcelFileReport(),
        responseType: 'blob' as 'json',
        params: params,
        observe: 'response',
        withCredentials: true
      }
    ).pipe(
      catchError(this.handleError('getCertificateStatisticDataInExcelFormat'))
    );
  }
  requestIdentification(identificatorForm: any, companyCmrId: any): Observable<any> {
    return this.http.post<any>(
      AppConfig.endpoints.identificationCallback.replace('{companyCmrId}', `${companyCmrId}`), identificatorForm,
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('requestIdentification'))
    );
  }
  requestInformationIdentification(callbackId: any, companyCmrId: any) {
    return this.http.get<any>(
      AppConfig.endpoints.identificationId.replace('{companyCmrId}', `${companyCmrId}`).replace('{callbackId}', `${callbackId}`),
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('requestInformationIdentification'))
    );
  }

  getHistoryIdentificationTab(searchCriteria: any, companyCmrId: any) {
    const params: any = {
      startDate: searchCriteria.startDate || '',
      endDate: searchCriteria.endDate || '',
      identifier: searchCriteria.identificator || '',
      phone: searchCriteria.phone || '',
      email: searchCriteria.email || '',
      name: searchCriteria.name || '',
      hasPaging: true,
      offset: searchCriteria.offset ?? 0,
      limit: searchCriteria.limit ?? 10,
    };

    const options = this.headerHelper.paramsOptions(observeTypes.RESPONSE, params);

    return this.http.get<any>(
      AppConfig.endpoints.identificationHistory.replace('{companyCmrId}', `${companyCmrId}`),
      this.headerHelper.paramsOptions(observeTypes.RESPONSE, params)
    ).pipe(
      catchError(this.handleError('requestInformationIdentification'))
    );
  }

  getInformationHistory(companyCmrId: any): Observable<any> {
    return this.http.get<any>(
      AppConfig.endpoints.identificationHistory.replace('{companyCmrId}', `${companyCmrId}`),
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('getInformationHistory'))
    );
  }

  getDownloadUEI(companyCmrId: any, callbackId: any) {
    return this.http.get<any>(
      AppConfig.endpoints.downloadUEI.replace('{companyCmrId}', `${companyCmrId}`).replace('{callbackId}', `${callbackId}`),
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('getInformationHistory'))
    );
  }
  getIdentificationInExcelFormat(searchCriteria: any, companyCmrId: any): Observable<any> {
    const params = {
      startDate: searchCriteria.fromStartDate || '',
      endDate: searchCriteria.toStartDate || '',
      identifier: searchCriteria.identificator || '',
      phone: searchCriteria.phone || '',
      email: searchCriteria.email || '',
      //BT-1494(Conflict resolution)
      //hasPaging: true,
      //offset: searchCriteria.offset,
      //limit: searchCriteria.limit,
      //order: searchCriteria.order,
      //orderBy: 'PHONE'
      hasPaging: false,
      // offset: searchCriteria.offset,
      // limit: searchCriteria.limit,
      // order: searchCriteria.order,
      // orderBy: 'PHONE'
    };

    const baseOptions = this.headerHelper.paramsOptions(observeTypes.RESPONSE, params);

    const httpOptions = {
      ...baseOptions,
      responseType: 'blob' as 'json',
      observe: 'response' as const,
      withCredentials: true
    };

    return this.http.get(
      AppConfig.endpoints.identificationExcel.replace('{companyCmrId}', `${companyCmrId}`),
      httpOptions
    ).pipe(
      catchError(this.handleError('getCertificateStatisticDataInExcelFormat'))
    );
  }
  requestWEbIdentification(identificatorForm: any, companyCmrId: any): Observable<any> {
    return this.http.post<any>(
      AppConfig.endpoints.webIdentification.replace('{companyCmrId}', `${companyCmrId}`), identificatorForm,
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('requestWebIdentification'))
    );
  }
  getWEbInformationHistory(companyCmrId: any): Observable<WebIdentificationHistoryResponse | any> {
    return this.http.get<WebIdentificationHistoryResponse>(
      AppConfig.endpoints.webIdentificationHistory.replace('{companyCmrId}', `${companyCmrId}`),
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('getWebInformationHistory'))
    );
  }
  getRequestsWebIdentification(companyCmrId: any,searchCriteria: any): Observable<RequestsWebIdentificationResponse | any> {
    const params = {
      expired: false,
            offset: searchCriteria.offset,
      limit: searchCriteria.limit,
    }
    return this.http.get<RequestsWebIdentificationResponse>(
      AppConfig.endpoints.requestsWebIdentification.replace('{companyCmrId}', `${companyCmrId}`),
      this.headerHelper.paramsOptions(observeTypes.RESPONSE, params)
    ).pipe(
      catchError(this.handleError('getWebInformationHistory'))
    );
  }
  getTabHistoryWebIdentification(companyCmrId: any, searchCriteria: any): Observable<WebIdentificationHistoryResponse | any> { 
    const params = {
      expired: searchCriteria.expired,
      offset: searchCriteria.offset,
      limit: searchCriteria.limit,
      fromDate: searchCriteria.fromDate || '',
      toDate: searchCriteria.toDate || '',
      phone: searchCriteria.phone || '',
      email: searchCriteria.email || '',
      name: searchCriteria.name || '',
      sortDirectionByCreatedAt: searchCriteria.sortDirectionByCreatedAt || null,
      sortDirectionByName: searchCriteria.sortDirectionByName || null,
      companyRelyingPartyId: searchCriteria.companyRelyingPartyId || ''
    }
    if (params.expired === null || params.expired === undefined) {
      delete params.expired;
    }
    if (params.sortDirectionByCreatedAt === null || params.sortDirectionByCreatedAt === undefined) {
      delete params.sortDirectionByCreatedAt;
    }
    if (params.sortDirectionByName === null || params.sortDirectionByName === undefined) {
      delete params.sortDirectionByName;
    }
    return this.http.get<WebIdentificationHistoryResponse>(
      AppConfig.endpoints.webIdentificationHistory.replace('{companyCmrId}', `${companyCmrId}`),
      this.headerHelper.paramsOptions(observeTypes.RESPONSE, params)
    ).pipe(
      catchError(this.handleError('getWebInformationHistory'))
    );
  }
  setIdentificatorData(data: any[]) {
    this.identificatorDataSubject.next(data);
  }

  getDownloadUEISIGN(companyCmrId: any,resultId: any, signsessionId: any): Observable<any> {  
    return this.http.get<any>(
      AppConfig.endpoints.downloadUEISign.replace('{companyCmrId}', `${companyCmrId}`).replace('{resultId}', `${resultId}`).replace('{signsessionId}', `${signsessionId}`),
       this.headerHelper.fileOptions(observeTypes.RESPONSE)
    ).pipe(
      catchError(this.handleError('getDownloadUEISIGN'))
    );
  }
  getDownloadUEIEXPIRED(companyCmrId: any, resultId: any, signsessionId: any): Observable<any> {
    return this.http.get<any>(
      AppConfig.endpoints.downloadUEIExpired.replace('{companyCmrId}', `${companyCmrId}`).replace('{resultId}', `${resultId}`).replace('{signsessionId}', `${signsessionId}`),
      this.headerHelper.fileOptions(observeTypes.RESPONSE)
    ).pipe(
      catchError(this.handleError('getDownloadUEIEXPIRED'))
    );
  }
    getDownloadIdentification(companyCmrId: any,resultId: any, signsessionId: any): Observable<any> {  
    return this.http.get<any>(
      AppConfig.endpoints.downloadIdentification.replace('{companyCmrId}', `${companyCmrId}`).replace('{resultId}', `${resultId}`).replace('{signsessionId}', `${signsessionId}`),
       this.headerHelper.fileOptions(observeTypes.RESPONSE)
    ).pipe(
      catchError(this.handleError('getDownloadUEISIGN'))
    );
  }
  getDocumentList(companyCmrId: any,resultId: any): Observable<any> {  
    return this.http.get<any>(
      AppConfig.endpoints.documentList.replace('{companyCmrId}', `${companyCmrId}`).replace('{resultId}', `${resultId}`),
       this.headerHelper.fileOptions(observeTypes.RESPONSE)
    ).pipe(
      catchError(this.handleError('getDownloadUEISIGN'))
    );
  }
  getDownloadUEICREATE(companyCmrId: any, resultId: any, websessionId: any): Observable<any> {
       return this.http.get<any>(
      AppConfig.endpoints.downloadUEICreate.replace('{companyCmrId}', `${companyCmrId}`).replace('{resultId}', `${resultId}`).replace('{webssionId}', `${websessionId}`), 
      this.headerHelper.fileOptions(observeTypes.RESPONSE)
   
    ).pipe(
      catchError(this.handleError('getDownloadUEICREATE'))
    );
  }
  getDownloadDocumentWeb(signsessionId: any): Observable<any> {
       return this.http.get<any>(
      AppConfig.endpoints.downloadUEICreate.replace('{signsessionId}', `${signsessionId}`), 
      this.headerHelper.fileOptions(observeTypes.RESPONSE)
   
    ).pipe(
      catchError(this.handleError('getDownloadUEICREATE'))
    );
  }
  getPermissionForCurrentUser(companyCmrId: string, userCmrId: string) {
    const url = AppConfig.endpoints.userAccess
      .replace('{companyId}', companyCmrId)
      .replace('{cmrId}', userCmrId);

    return this.http.get<ISpringResponseBody>(url, this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('getPermissionForCurrentUser'))
    );
  }

  public getRequestedFile(_signDocReqId: string): Observable<any> {
    return this.http.get(
      AppConfig.endpoints.storage.getRequestedFile + '/' + _signDocReqId,
      this.headerHelper.fileOptions(observeTypes.RESPONSE, 'blob')
    ).pipe(
      catchError(this.handleError('getFile'))
    );
  }

  buildRequestOptions(options: Object) {
    let params = new HttpParams();
    Object.keys(options).forEach(key => {
      params = params.set(key, options[key]);
    });
    const requestOptions = this.headerHelper.paramsOptions(observeTypes.BODY, params);
    return requestOptions;
  }

  rowSubject = new BehaviorSubject<any>(this.getStoredRow());

  getStoredRow(): any {
    const data = sessionStorage.getItem('hardwareCert');
    return data ? JSON.parse(data) : null;
  }

  setRow(row: any): void {
    sessionStorage.setItem('hardwareCert', JSON.stringify(row));
    this.rowSubject.next(row);
  }

  getRow(): Observable<any> {
    return this.rowSubject.asObservable();
  }

  rowSubjectHistory = new BehaviorSubject<any>(this.getStoredRowHistory());

  getStoredRowHistory(): any {
    const data = sessionStorage.getItem('historyCert');
    return data ? JSON.parse(data) : null;
  }

  setRowHistory(row: any): void {
    sessionStorage.setItem('historyCert', JSON.stringify(row));
    this.rowSubjectHistory.next(row);
  }

  public createAndProcessCqesManagementRequest(rowData: any, requestData: any) { 
    const url = AppConfig.endpoints.hardwareCert.createAndProcessCqesManagementRequest;
    let company = JSON.parse(sessionStorage.getItem('cqes-currentCompany'));
    let userData = JSON.parse(sessionStorage.getItem('cqes-loginEventData')).userData;
    return this.http.post<any>(
      url,
      {
        ...requestData,
        requestCreatorFullName: `${userData.firstName.bg} ${userData.lastName.bg}`,
        requestCreatorCmrId: userData.cmrId,
        requestCreatorCompanyCmrId: company.cmrId,
        requestCreatorCompany: { bg: company.nameBg, en: company.nameEn }
      },
      this.headerHelper.optionsWithCustomHeaders(observeTypes.RESPONSE, {
        'BTrust-Request-ID': `${rowData.requestNumber}`,
        'Customer-Request-ID': `${rowData.customerRequestId}`
      })
    )
      .pipe(
        catchError(this.handleError('createAndProcessCqesManagementRequest'))
      );
  
  }

  public completeCqesManagementRequest(rowData: any, file: File, filename: string, signedData: string) {
    const url = AppConfig.endpoints.hardwareCert.completeCqesManagementRequest;
    const btrustRevokeRequestId = JSON.parse(sessionStorage.getItem('btrustRevokeRequestId'));

    const formData = new FormData();
    formData.append('applicationFile', file);
    formData.append('applicationFileName', filename);
    formData.append('signedData', new Blob([JSON.stringify({ signedData: signedData })], { type: 'application/json' }));

    return this.http.post<any>(
      url,
      formData,
      this.headerHelper.optionsWithCustomHeaders(observeTypes.RESPONSE, {
        'BTrust-Request-ID': `${rowData.requestNumber}`,
        'BTrust-Revoke-Request-ID': `${btrustRevokeRequestId}`,
        'Customer-Request-ID': `${rowData.customerRequestId}`
      })
    )
      .pipe(
        catchError(this.handleError('completeCqesManagementRequest'))
      );
  }

  // managment request
  searchForManagementRequests(params: HttpParams): Observable<ISpringResponseBody | any> {
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.searchForManagementRequests,
      this.headerHelper.paramsOptions(observeTypes.RESPONSE, params)
    ).pipe(
      catchError(this.handleError('searchForManagementRequests'))
    );
  }

  getManagementRequestsExcelFormat(searchCriteria: SearchCertificateCriteria): Observable<any> {
    const params = {
      hasPaging: false,
      ...searchCriteria
    };

    return this.http.get(
      AppConfig.endpoints.createReportForManagementRequestsExcell,
      {
        headers: this.headerHelper.downloadExcelFileReport(),
        responseType: 'json',
        params: params,
        observe: 'response',
        withCredentials: true
      }
    ).pipe(
      catchError(this.handleError('getManagementRequestsExcelFormat'))
    );
  }

  getManagementHistory(serialNumber): Observable<any> {
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.managementHistory.replace('{serialNumber}', `${serialNumber}`),
      this.headerHelper.options((observeTypes.BODY))
    ).pipe(
      catchError(this.handleError('getManagementHistory'))
    );
  }

  getManagementHistoryExcelFormat(serialNumber): Observable<any> {
    return this.http.get(
      AppConfig.endpoints.managementHistorysExcell.replace('{serialNumber}', `${serialNumber}`),
      {
        headers: this.headerHelper.downloadExcelFileReport(),
        responseType: 'json',
        observe: 'response',
        withCredentials: true
      }
    ).pipe(
      catchError(this.handleError('getManagementHistoryExcelFormat'))
    );
  }
  
  getReportsUsersCompany(searchCriteria, companyCmrId: any,) {
    console.log('searchCriteria', searchCriteria)
    let params = {    
      role: Array.isArray(searchCriteria.role) ? searchCriteria.role.join(',') : searchCriteria.role,
      service: Array.isArray(searchCriteria.service) ? searchCriteria.service.join(',') : searchCriteria.service,
      status: Array.isArray(searchCriteria.status) ? searchCriteria.status.map((status:any) => status.value).join(',') : searchCriteria.status,
      fromDate: searchCriteria.fromDate ?? '',
      toDate: searchCriteria.toDate ?? '',
      hasPaging: true,
      offset: searchCriteria.offset,
      limit: searchCriteria.limit,
      registrationDateOrder: searchCriteria.registrationDateOrder ?? ''
    };

    if (params.fromDate===''){
      delete params.fromDate;
    }
    if (params.toDate===''){
      delete params.toDate;
    }
    if (params.role===''){
      delete params.role;
    }
    if (params.service===''){
      delete params.service;
    }
    if (params.status===''){
      delete params.status;
    }
    if (params.registrationDateOrder===''){
      delete params.registrationDateOrder;
    }
        
    return this.http.get<any>(
      AppConfig.endpoints.reports.allReports.replace('{companyCmrId}', `${companyCmrId}`),
      this.headerHelper.paramsOptions(observeTypes.RESPONSE, params)
    ).pipe(
      catchError(this.handleError('getReportsUsersCompany'))
    );
  }
  getUserRoles() {
    return this.http.get<any>(
      AppConfig.endpoints.rolesUser,
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('getReportsUsersCompany'))
    );
  }

  getReportInExcelFormat(searchCriteria: any, companyCmrId: any): Observable<any> {
    const params = {     
      role: Array.isArray(searchCriteria.role) ? searchCriteria.role.join(',') : searchCriteria.role,
      service: Array.isArray(searchCriteria.service) ? searchCriteria.service.join(',') : searchCriteria.service,
      hasPaging: false,
      offset: searchCriteria.offset,
      limit: searchCriteria.limit,
    }
    const baseOptions = this.headerHelper.paramsOptions(observeTypes.RESPONSE, params);

    const httpOptions = {
      ...baseOptions,
      responseType: 'json' as const,
      observe: 'response' as const,
      withCredentials: true,
    };

    return this.http.get(
      AppConfig.endpoints.reports.reportExcel.replace('{companyCmrId}', `${companyCmrId}`),
      httpOptions
    ).pipe(
      catchError(this.handleError('getCertificateStatisticDataInExcelFormat'))
    );
  }
}
